"use client";

import type { UseTTSPlayerReturn } from "@/app/lib/hooks/useTTSPlayer";

// Floating transport bar — bottom-anchored overlay shown while a card
// is playing or paused. Hidden in idle/error. Five controls + ✕ on the
// bottom row, scrubber on top. Mirrors iOS TTSTransportBar.swift layout
// without the SwiftUI-specific bits.

interface TTSTransportBarProps {
  player: UseTTSPlayerReturn;
}

function fmtTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00";
  const s = Math.floor(seconds);
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

export default function TTSTransportBar({ player }: TTSTransportBarProps) {
  const { state, currentTime, duration, speed, error } = player;
  const isVisible = state === "playing" || state === "paused" || state === "fetching" || state === "error";
  if (!isVisible) return null;

  const isPlaying = state === "playing";
  const isFetching = state === "fetching";
  const progress = duration > 0 ? Math.min(1, currentTime / duration) : 0;

  return (
    <div
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: "var(--bottom-bar-h, 120px)",
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
        zIndex: 50,
      }}
      role="region"
      aria-label="TTS playback controls"
    >
      <div
        style={{
          pointerEvents: "auto",
          background: "rgba(28, 33, 41, 0.96)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "10px 14px",
          // 70% of the prior 560px max. Narrower bar reads as "control
          // chip" rather than a full toolbar.
          width: "min(392px, calc(100vw - 24px))",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {/* Scrubber row */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontVariantNumeric: "tabular-nums", minWidth: 30 }}>
            {fmtTime(currentTime)}
          </span>
          <div
            style={{
              flex: 1,
              height: 3,
              borderRadius: 2,
              background: "var(--border)",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: `${progress * 100}%`,
                background: "var(--accent)",
                transition: "width 120ms linear",
              }}
            />
          </div>
          <span style={{ fontSize: 11, color: "var(--text-tertiary)", fontVariantNumeric: "tabular-nums", minWidth: 30, textAlign: "right" }}>
            {fmtTime(duration)}
          </span>
        </div>

        {error && (
          <div style={{ fontSize: 11, color: "var(--error)", textAlign: "center" }}>
            {error}
          </div>
        )}

        {/* Control row: speed pinned LEFT · the 4 transport buttons
            (−15, play/pause, +30, skip-end) centered as a group via
            flex:1 spacers on both sides · ✕ pinned RIGHT. */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          {/* Speed cycle — far left */}
          <button
            type="button"
            className="btn-ghost"
            onClick={player.cycleSpeed}
            disabled={isFetching}
            title="Playback speed"
            aria-label={`Playback speed ${speed}x`}
            style={{
              minWidth: 44,
              height: 44,
              fontSize: 12,
              fontWeight: 600,
              fontVariantNumeric: "tabular-nums",
              color: "var(--text-primary)",
            }}
          >
            {speed}×
          </button>

          {/* Left spacer — pushes the 4-button transport group to the
              center of the bar regardless of speed-label width. */}
          <span style={{ flex: 1 }} />

          {/* −15s */}
          <button
            type="button"
            className="btn-ghost"
            onClick={() => player.skip(-15)}
            disabled={isFetching || state === "error"}
            title="Back 15 seconds"
            aria-label="Skip back 15 seconds"
            style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--text-primary)" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 12a9 9 0 1 0 9-9" />
              <path d="M3 4v5h5" />
              <text x="12" y="15" fontSize="7" fontWeight="700" fill="currentColor" stroke="none" textAnchor="middle">15</text>
            </svg>
          </button>

          {/* Play / Pause — center, slightly larger */}
          <button
            type="button"
            className="btn-ghost"
            onClick={() => (isPlaying ? player.pause() : player.resume())}
            disabled={isFetching || state === "error"}
            title={isPlaying ? "Pause" : "Play"}
            aria-label={isPlaying ? "Pause playback" : "Resume playback"}
            style={{
              width: 48,
              height: 48,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              color: "var(--text-primary)",
            }}
          >
            {isFetching ? (
              <svg width="22" height="22" viewBox="0 0 16 16" fill="none" style={{ animation: "spin 1.2s linear infinite" }}>
                <circle cx="8" cy="8" r="6.5" stroke="var(--text-secondary)" strokeWidth="1.5" opacity="0.25" />
                <path d="M14.5 8a6.5 6.5 0 0 0-6.5-6.5" stroke="var(--text-secondary)" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ) : isPlaying ? (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5l11 7-11 7V5z" />
              </svg>
            )}
          </button>

          {/* +30s */}
          <button
            type="button"
            className="btn-ghost"
            onClick={() => player.skip(30)}
            disabled={isFetching || state === "error"}
            title="Forward 30 seconds"
            aria-label="Skip forward 30 seconds"
            style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--text-primary)" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 1 1-9-9" />
              <path d="M21 4v5h-5" />
              <text x="12" y="15" fontSize="7" fontWeight="700" fill="currentColor" stroke="none" textAnchor="middle">30</text>
            </svg>
          </button>

          {/* Skip to end */}
          <button
            type="button"
            className="btn-ghost"
            onClick={player.skipToEnd}
            disabled={isFetching || state === "error"}
            title="Skip to end"
            aria-label="Skip to end"
            style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--text-primary)" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
              <path d="M4 5l11 7-11 7V5z" />
              <rect x="17" y="5" width="3" height="14" rx="0.5" />
            </svg>
          </button>

          {/* Right spacer — completes the centering of the 4-button
              transport group (with the left spacer above the −15).
              Anything after this is pinned flush-right. */}
          <span style={{ flex: 1 }} />

          {/* ✕ flush right */}
          <button
            type="button"
            className="btn-ghost"
            onClick={player.stop}
            title="Close"
            aria-label="Stop and close transport bar"
            style={{ width: 44, height: 44, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "var(--text-primary)", opacity: 0.85 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
